import React, { useEffect, useRef } from "react";
import { Box, useTheme } from "@mui/material";
import moment, { Moment } from "moment-timezone";

const ITEM_H = 44; // row height
const VISIBLE = 3; // visible rows (must be odd so one is centered)

interface WheelColumnProps {
  items: string[];
  index: number;
  onIndexChange: (i: number) => void;
  minWidth: number;
  /** Accessible name for the column (e.g. "Hours"). */
  label: string;
}

const WheelColumn: React.FC<WheelColumnProps> = ({
  items,
  index,
  onIndexChange,
  minWidth,
  label,
}) => {
  const theme = useTheme();
  const m = theme.palette.mobile;
  const ref = useRef<HTMLDivElement>(null);
  const settleRef = useRef<any>(null);

  // Position to the selected row once on mount.
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = index * ITEM_H;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToIndex = (i: number) => {
    const clamped = Math.max(0, Math.min(items.length - 1, i));
    ref.current?.scrollTo({ top: clamped * ITEM_H, behavior: "smooth" });
    if (clamped !== index) onIndexChange(clamped);
  };

  const handleScroll = () => {
    if (!ref.current) return;
    clearTimeout(settleRef.current);
    settleRef.current = setTimeout(() => {
      const el = ref.current!;
      const i = Math.max(
        0,
        Math.min(items.length - 1, Math.round(el.scrollTop / ITEM_H)),
      );
      el.scrollTo({ top: i * ITEM_H, behavior: "smooth" });
      if (i !== index) onIndexChange(i);
    }, 110);
  };

  // Keyboard operability for the custom wheel (WCAG 2.1.1): expose each column
  // as a spinbutton so arrow/Home/End keys move through the values.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case "ArrowUp":
        e.preventDefault();
        scrollToIndex(index - 1);
        break;
      case "ArrowDown":
        e.preventDefault();
        scrollToIndex(index + 1);
        break;
      case "Home":
        e.preventDefault();
        scrollToIndex(0);
        break;
      case "End":
        e.preventDefault();
        scrollToIndex(items.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <Box
      ref={ref}
      onScroll={handleScroll}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="spinbutton"
      aria-label={label}
      aria-valuenow={index}
      aria-valuemin={0}
      aria-valuemax={items.length - 1}
      aria-valuetext={items[index]}
      sx={{
        height: ITEM_H * VISIBLE,
        minWidth,
        overflowY: "auto",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": { display: "none" },
        "&:focus-visible": {
          outline: `2px solid ${m.ePurple[500]}`,
          outlineOffset: "-2px",
          borderRadius: "8px",
        },
      }}
    >
      <Box aria-hidden="true" sx={{ height: ITEM_H }} />
      {items.map((it, i) => (
        <Box
          key={it}
          aria-hidden="true"
          onClick={() =>
            ref.current?.scrollTo({ top: i * ITEM_H, behavior: "smooth" })
          }
          sx={{
            height: ITEM_H,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            fontSize: i === index ? "1.5rem" : "1.25rem",
            fontWeight: i === index ? 700 : 500,
            color: i === index ? m.base[900] : m.base[100],
            transition: "color 0.15s ease, font-size 0.15s ease",
          }}
        >
          {it}
        </Box>
      ))}
      <Box aria-hidden="true" sx={{ height: ITEM_H }} />
    </Box>
  );
};

interface MobileWheelTimePickerProps {
  value: Moment | null;
  onChange: (v: Moment) => void;
}

const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));
const AMPM = ["am", "pm"];

const MobileWheelTimePicker: React.FC<MobileWheelTimePickerProps> = ({
  value,
  onChange,
}) => {
  const theme = useTheme();
  const m = theme.palette.mobile;

  const base = value || moment();
  const hour24 = base.hour();
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const hourIndex = hour12 - 1;
  const minuteIndex = base.minute();
  const ampmIndex = hour24 < 12 ? 0 : 1;

  const emit = (h12: number, minute: number, ampm: number) => {
    const h24 = (h12 % 12) + (ampm === 1 ? 12 : 0);
    onChange((value ? value.clone() : moment()).hour(h24).minute(minute).second(0));
  };

  const Colon = (
    <Box
      aria-hidden="true"
      sx={{
        display: "flex",
        alignItems: "center",
        fontSize: "1.5rem",
        fontWeight: 700,
        color: m.base[900],
        px: 0.5,
      }}
    >
      :
    </Box>
  );

  return (
    <Box
      role="group"
      aria-label="Time picker"
      sx={{
        position: "relative",
        border: `1px solid ${m.surface[100]}`,
        borderRadius: "14px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          justifyContent: "center",
          gap: 1,
          py: 0.5,
        }}
      >
        <WheelColumn
          items={HOURS}
          index={hourIndex}
          onIndexChange={(i) => emit(i + 1, minuteIndex, ampmIndex)}
          minWidth={56}
          label="Hours"
        />
        {Colon}
        <WheelColumn
          items={MINUTES}
          index={minuteIndex}
          onIndexChange={(i) => emit(hour12, i, ampmIndex)}
          minWidth={56}
          label="Minutes"
        />
        <WheelColumn
          items={AMPM}
          index={ampmIndex}
          onIndexChange={(i) => emit(hour12, minuteIndex, i)}
          minWidth={64}
          label="AM or PM"
        />
      </Box>

      {/* Center selection band */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "50%",
          height: ITEM_H,
          transform: "translateY(-50%)",
          borderTop: `1px solid ${m.surface[100]}`,
          borderBottom: `1px solid ${m.surface[100]}`,
          pointerEvents: "none",
        }}
      />
    </Box>
  );
};

export default MobileWheelTimePicker;
